<template>
  <transition name="fade">
    <div v-if="open" class="modal-backdrop" @click.self="onCancel">
      <div class="modal-card beneficiary-modal">
        <div class="modal-header-row">
          <h3 class="modal-title">{{ isEditMode ? "Edit Beneficiary" : "Add Beneficiary" }}</h3>
          <button
            type="button"
            class="icon-btn"
            aria-label="Close"
            :disabled="saving"
            @click="onCancel"
          >
            <i class="ri-close-line"></i>
          </button>
        </div>

        <div class="form-box beneficiary-form">
          <div class="fgroup">
            <label>Full Name <span class="req">*</span></label>
            <input
              v-model.trim="form.fullName"
              type="text"
              placeholder="Beneficiary full name"
              :disabled="saving"
            />
          </div>

          <div class="fgroup">
            <label>Email <span class="req">*</span></label>
            <input
              v-model.trim="form.emailId"
              type="email"
              placeholder="Email address"
              :disabled="saving"
            />
          </div>

          <div class="fgroup">
            <label>Mobile Number <span class="req">*</span></label>
            <input
              v-model="form.mobileNumber"
              type="text"
              inputmode="numeric"
              placeholder="10-digit mobile"
              maxlength="10"
              :disabled="saving"
              @input="onMobileInput"
            />
          </div>

          <div class="fgroup">
            <label>Relationship <span class="req">*</span></label>
            <select v-model="form.relationship" :disabled="saving">
              <option value="" disabled>Select relationship</option>
              <option
                v-for="option in relationshipOptions"
                :key="option"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
          </div>

          <div v-if="isOtherSelected" class="fgroup">
            <label>Other Relationship <span class="req">*</span></label>
            <input
              v-model.trim="form.otherRelationship"
              type="text"
              placeholder="Enter relationship"
              :disabled="saving"
            />
          </div>

          <p v-if="error" class="field-error">{{ error }}</p>

          <div class="form-actions">
            <button
              type="button"
              class="cancel-btn"
              :disabled="saving"
              @click="onCancel"
            >
              Cancel
            </button>
            <button
              type="button"
              class="save-btn"
              :disabled="saving"
              @click="onSubmit"
            >
              {{ saving ? "Saving..." : isEditMode ? "Update" : "Add Beneficiary" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { defineProps, defineEmits } from "vue";

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: "add",
  },
  initialData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["cancel", "submit"]);

const relationshipOptions = [
  "Brother",
  "Father",
  "Mother",
  "Sister",
  "Spouse",
  "Others",
];

const form = ref({
  fullName: "",
  emailId: "",
  mobileNumber: "",
  relationship: "",
  otherRelationship: "",
});

const error = ref("");
const isEditMode = computed(() => props.mode === "edit");

const isOtherSelected = computed(() => form.value.relationship === "Others");

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      applyInitialData();
    }
  },
);

watch(
  () => props.initialData,
  () => {
    if (props.open) {
      applyInitialData();
    }
  },
  { deep: true },
);

watch(
  () => form.value.relationship,
  (value) => {
    if (value !== "Others") {
      form.value.otherRelationship = "";
    }
  },
);

function resetForm() {
  error.value = "";
  form.value = {
    fullName: "",
    emailId: "",
    mobileNumber: "",
    relationship: "",
    otherRelationship: "",
  };
}

function applyInitialData() {
  resetForm();

  const data = props.initialData || {};
  const fullName = String(data.fullName || "").trim();
  const emailId = String(data.emailId || "").trim();
  const mobileNumber = String(data.mobileNumber || "").trim();
  const rawRelationship = String(data.relationship || "").trim();

  if (!rawRelationship) {
    form.value = {
      ...form.value,
      fullName,
      emailId,
      mobileNumber,
    };
    return;
  }

  const matchedOption = relationshipOptions.find(
    (option) => option.toLowerCase() === rawRelationship.toLowerCase(),
  );

  if (matchedOption) {
    form.value = {
      ...form.value,
      fullName,
      emailId,
      mobileNumber,
      relationship: matchedOption,
      otherRelationship: "",
    };
    return;
  }

  form.value = {
    ...form.value,
    fullName,
    emailId,
    mobileNumber,
    relationship: "Others",
    otherRelationship: rawRelationship,
  };
}

function onMobileInput() {
  form.value.mobileNumber = String(form.value.mobileNumber || "").replace(
    /\D/g,
    "",
  );
}

function validateForm() {
  const payload = {
    fullName: String(form.value.fullName || "").trim(),
    emailId: String(form.value.emailId || "").trim(),
    mobileNumber: String(form.value.mobileNumber || "").trim(),
    relationship:
      form.value.relationship === "Others"
        ? String(form.value.otherRelationship || "").trim()
        : String(form.value.relationship || "").trim(),
  };

  if (
    !payload.fullName ||
    !payload.emailId ||
    !payload.mobileNumber ||
    !payload.relationship
  ) {
    error.value = "All fields are required.";
    return null;
  }

  if (!/^\d{10}$/.test(payload.mobileNumber)) {
    error.value = "Enter a valid 10-digit mobile number.";
    return null;
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(payload.emailId)) {
    error.value = "Enter a valid email address.";
    return null;
  }

  error.value = "";
  return payload;
}

function onCancel() {
  if (props.saving) return;
  emit("cancel");
}

function onSubmit() {
  const payload = validateForm();
  if (!payload) return;
  emit("submit", payload);
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 34, 64, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 2100;
}
.beneficiary-modal {
  width: 100%;
  max-width: 520px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #dce7f6;
  box-shadow: 0 18px 40px rgba(10, 34, 64, 0.2);
  padding: 16px;
}
.modal-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.modal-title {
  margin: 0;
  font-size: 18px;
  color: #0a2240;
}
.beneficiary-form {
  margin-bottom: 0;
}
.fgroup {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.fgroup label {
  font-size: 12px;
  font-weight: 700;
  color: #0a2240;
}
.fgroup input,
.fgroup select {
  height: 40px;
  padding: 0 12px;
  font-size: 13px;
  color: #333f48;
  border: 1px solid rgba(10, 34, 64, 0.12);
  border-radius: 8px;
  background: #fff;
  outline: none;
  transition: all 0.2s;
  font-family: inherit;
  box-sizing: border-box;
  width: 100%;
}
.fgroup input:focus,
.fgroup select:focus {
  border-color: #003da5;
  box-shadow: 0 0 0 3px rgba(0, 61, 165, 0.08);
}
.form-box {
  background: #f8fafc;
  border: 1px solid #dce7f6;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.form-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 2px;
}
.cancel-btn {
  height: 36px;
  padding: 0 18px;
  background: transparent;
  border: 1.5px solid #d1dbe8;
  color: #6b7f99;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
}
.cancel-btn:hover {
  border-color: #003da5;
  color: #003da5;
}
.save-btn {
  height: 36px;
  padding: 0 20px;
  background: #ffc72c;
  border: none;
  color: #0a2240;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
}
.save-btn:hover:not(:disabled) {
  background: #003da5;
  color: #fff;
}
.save-btn:disabled,
.cancel-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.icon-btn {
  width: 30px;
  height: 30px;
  border: 1px solid #e2eaf6;
  background: #f8fafc;
  color: #6b7f99;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.18s;
  flex-shrink: 0;
}
.req {
  color: #dc2626;
}
.field-error {
  margin: 0;
  font-size: 12px;
  color: #dc2626;
  font-weight: 600;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
